import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  min-height: 100vh;
  padding-top: 6rem;
  position: relative;
`;

const Section = styled.section`
  position: relative;
  padding: 4rem 1.5rem;
  background: ${({ theme }) => theme.colors.background};
`;

const SectionInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  gap: 3rem;
`;

const SectionHeader = styled.div`
  max-width: 820px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: center;
  margin: 0 auto;
`;

const Eyebrow = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.highlight};
`;

const SectionTitle = styled.h1`
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1rem;
`;

const SectionCopy = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
  align-self: flex-start;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const FilterTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
  background: ${({ theme }) => theme.colors.panelBackground};
  border-radius: 12px;
  padding: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.panelBorder};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const FilterTab = styled.button<{ isActive: boolean }>`
  background: ${({ isActive, theme }) => 
    isActive ? theme.colors.accent : 'transparent'};
  color: ${({ isActive, theme }) => 
    isActive ? theme.colors.white : theme.colors.text};
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  max-width: 150px;

  &:hover {
    background: ${({ isActive, theme }) => 
      isActive ? theme.colors.primary : theme.colors.cardBackground};
  }

  @media (max-width: 768px) {
    max-width: none;
  }
`;

const NewsEventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const NewsEventCard = styled.div<{ isFeatured?: boolean }>`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: 16px;
  padding: 2rem;
  border: ${({ isFeatured, theme }) => 
    isFeatured ? `2px solid ${theme.colors.accent}` : `1px solid ${theme.colors.cardBorder}`};
  box-shadow: ${({ theme, isFeatured }) => 
    isFeatured ? '0 12px 32px rgba(16, 185, 129, 0.15)' : theme.colors.cardShadow};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme, isFeatured }) => 
      isFeatured ? '0 20px 40px rgba(16, 185, 129, 0.25)' : '0 12px 32px rgba(0, 0, 0, 0.15)'};
  }
`;

const FeaturedBadge = styled.div`
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  align-self: flex-start;
`;

const TypeBadge = styled.div<{ type: 'news' | 'event' }>`
  background: ${({ type, theme }) => 
    type === 'event' ? '#8B5CF6' : theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  align-self: flex-start;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const CardTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.4rem;
  font-weight: 600;
  line-height: 1.3;
`;

const CardDate = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  font-weight: 500;
`;

const CardDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 0.5rem;
`;

const EventDetails = styled.div`
  background: rgba(139, 92, 246, 0.1);
  border-radius: 8px;
  padding: 1rem;
  margin: 0.5rem 0;
  border: 1px solid rgba(139, 92, 246, 0.2);
`;

const EventDetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const EventIcon = styled.span`
  font-size: 1rem;
  width: 20px;
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: auto;
  flex-wrap: wrap;
`;

const ViewButton = styled.a`
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const ExternalButton = styled.a`
  background: transparent;
  color: ${({ theme }) => theme.colors.accent};
  border: 1px solid ${({ theme }) => theme.colors.accent};
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.white};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 3rem;
  font-size: 1.1rem;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 3rem;
`;

const EmptyTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.4rem;
  margin-bottom: 1rem;
`;

// TypeScript interfaces
interface NewsEvent {
  id: number;
  title: string;
  description: string;
  content: string;
  type: 'news' | 'event';
  event_date?: string;
  event_location?: string;
  event_url?: string;
  featured: boolean;
  category: string;
  status: string;
  publish_date: string;
}

const NewsEvents: React.FC = () => {
  const [newsEvents, setNewsEvents] = useState<NewsEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<'all' | 'news' | 'event'>('event');

  useEffect(() => {
    fetchNewsEvents();
  }, []);

  const fetchNewsEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/news-events.php');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.items) {
          setNewsEvents(data.items);
        }
      }
    } catch (error) {
      console.error('Error fetching news events:', error);
      setNewsEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatEventDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const filteredNewsEvents = newsEvents
    .filter(item => item.status === 'published')
    .filter(item => activeFilter === 'all' || item.type === activeFilter)
    .sort((a, b) => {
      // Featured items first, then by publish date
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return new Date(b.publish_date).getTime() - new Date(a.publish_date).getTime();
    });

  return (
    <PageWrapper>
      <Section>
        <SectionInner>
          <BackButton to="/">← Back to Home</BackButton>
          
          <SectionHeader>
            <Eyebrow>News & Events</Eyebrow>
            <SectionTitle>The Latest Men's Well News & Events</SectionTitle>
            <SectionCopy>
              Stay up to date with our latest news, upcoming events, and community announcements. 
              Join us for our meetings and special events throughout the year.
            </SectionCopy>
          </SectionHeader>

          <FilterTabs>
            <FilterTab 
              isActive={activeFilter === 'all'}
              onClick={() => setActiveFilter('all')}
            >
              All Items
            </FilterTab>
            <FilterTab 
              isActive={activeFilter === 'news'}
              onClick={() => setActiveFilter('news')}
            >
              News
            </FilterTab>
            <FilterTab 
              isActive={activeFilter === 'event'}
              onClick={() => setActiveFilter('event')}
            >
              Events
            </FilterTab>
          </FilterTabs>

          {loading ? (
            <LoadingMessage>Loading news and events...</LoadingMessage>
          ) : filteredNewsEvents.length === 0 ? (
            <EmptyMessage>
              <EmptyTitle>No {activeFilter !== 'all' ? activeFilter : ''} items found</EmptyTitle>
              <p>Check back soon for updates on our latest news and events.</p>
            </EmptyMessage>
          ) : (
            <NewsEventsGrid>
              {filteredNewsEvents.map((item) => (
                <NewsEventCard key={item.id} isFeatured={item.featured}>
                  <CardHeader>
                    <div>
                      {item.featured && <FeaturedBadge>Featured</FeaturedBadge>}
                      <TypeBadge type={item.type}>
                        {item.type === 'event' ? '📅 Event' : '📰 News'}
                      </TypeBadge>
                    </div>
                    <CardDate>
                      {item.type === 'event' && item.event_date 
                        ? formatEventDate(item.event_date)
                        : formatDate(item.publish_date)}
                    </CardDate>
                  </CardHeader>

                  <CardTitle>{item.title}</CardTitle>
                  <CardDescription>{item.description}</CardDescription>

                  {item.type === 'event' && (
                    <EventDetails>
                      {item.event_date && (
                        <EventDetailRow>
                          <EventIcon>🕐</EventIcon>
                          <span>{formatEventDate(item.event_date)}</span>
                        </EventDetailRow>
                      )}
                      {item.event_location && (
                        <EventDetailRow>
                          <EventIcon>📍</EventIcon>
                          <span>{item.event_location}</span>
                        </EventDetailRow>
                      )}
                    </EventDetails>
                  )}

                  <CardActions>
                    <ViewButton 
                      onClick={() => {
                        // For now, expand content in place. Later could be modal or separate page.
                        alert(item.content);
                      }}
                    >
                      Read More
                    </ViewButton>
                    {item.event_url && (
                      <ExternalButton 
                        href={item.event_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Event
                      </ExternalButton>
                    )}
                  </CardActions>
                </NewsEventCard>
              ))}
            </NewsEventsGrid>
          )}
        </SectionInner>
      </Section>
    </PageWrapper>
  );
};

export default NewsEvents;